import React, { useEffect } from "react";
import type { CSSProperties } from "react";
import {
  accent10a as accent, accent10aRgb, tint10a as tint, useSlide10a, Seg,
} from "./slide10a.data";
import { FONT_BODY, FONT_DISPLAY, FONT_MONO } from "theme/fonts";

/* Portrait/mobile layout for Slide 10a in ALL mode — flowing, scrollable, no canvas.
   (The Practice-mode version keeps the interactive humming guide instead.) */

const font = FONT_BODY;
const mono = FONT_MONO;

const lbl: CSSProperties = { fontFamily: mono, fontSize: 10, letterSpacing: 2.5, color: accent, textTransform: "uppercase", margin: "0 0 8px" };
const txt: CSSProperties = { fontFamily: FONT_BODY, fontStyle: "normal", fontSize: 15, lineHeight: 1.7, color: "#cde0f2", margin: 0 };

const renderSegs = (segs: Seg[], boldColor: string) =>
  segs.map((s, i) =>
    s.b ? <b key={i} style={{ color: boldColor, fontWeight: 700 }}>{s.t}</b>
    : s.em ? <em key={i}>{s.t}</em>
    : <React.Fragment key={i}>{s.t}</React.Fragment>
  );

const HTLDSlide10aMobile: React.FC = () => {
  const { meta: slide10aMeta, steps: slide10aSteps, what: slide10aWhat, duration: slide10aDuration } = useSlide10a();
  // Load the font once; never removed, other slides share it.
  useEffect(() => {
    if (!document.querySelector('link[href*="Space+Grotesk"]')) {
      const l = document.createElement("link"); l.rel = "stylesheet";
      l.href = "https://fonts.googleapis.com/css2?family=Merriweather:ital,wght@0,300;0,400;0,700;1,300;1,400;1,700&display=swap";
      document.head.appendChild(l);
    }
  }, []);

  return (
    <div style={{ background: "#040f18", color: "#d0e8f8", fontFamily: font, padding: "22px 18px 40px", boxSizing: "border-box", height: "100%", width: "100%", overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
      {/* breadcrumb */}
      <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: 2, color: tint.crumbMuted, textTransform: "uppercase", marginBottom: 8 }}>
        {slide10aMeta.breadcrumbPre} · <span style={{ color: accent }}>{slide10aMeta.breadcrumbAccent}</span>
      </div>

      {/* sanskrit + title */}
      <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: 3, color: `rgba(${accent10aRgb},0.55)`, marginBottom: 6 }}>{slide10aMeta.sanskrit}</div>
      <h1 style={{ fontFamily: FONT_DISPLAY, fontStyle: "normal", fontWeight: 600, fontSize: 25, lineHeight: 1.2, margin: "0 0 4px", color: "#e8f4ff" }}>
        {slide10aMeta.titlePre}<span style={{ color: accent }}>{slide10aMeta.titleAccentWord}</span>{slide10aMeta.titlePost}
      </h1>
      <p style={{ fontFamily: FONT_BODY, fontStyle: "normal", fontWeight: 300, fontSize: 14, color: tint.subMuted, margin: "0 0 20px", lineHeight: 1.5 }}>
        {slide10aMeta.subtitle}
      </p>

      {/* what it does */}
      <div style={{ marginBottom: 18 }}>
        <div style={lbl}>{slide10aMeta.whatLabel}</div>
        <p style={txt}>{renderSegs(slide10aWhat, tint.bold)}</p>
      </div>

      {/* how to practise */}
      <div style={{ marginBottom: 18 }}>
        <div style={lbl}>{slide10aMeta.howLabel}</div>
        {slide10aSteps.map((s, i) => (
          <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 10 }}>
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: accent, flexShrink: 0, marginTop: 7, opacity: 0.75 }} />
            <div>
              <span style={{ fontFamily: FONT_BODY, fontStyle: "normal", fontWeight: 700, fontSize: 14.5, color: tint.step }}>{s.step} </span>
              <span style={{ fontFamily: FONT_BODY, fontStyle: "normal", fontWeight: 300, fontSize: 14.5, lineHeight: 1.55, color: "#9bbdd6" }}>{s.desc}</span>
            </div>
          </div>
        ))}
      </div>

      {/* duration callout */}
      <div style={{ background: `rgba(${accent10aRgb},0.06)`, border: `1px solid rgba(${accent10aRgb},0.22)`, borderRadius: 10, padding: "14px 16px" }}>
        <div style={lbl}>{slide10aMeta.durationLabel}</div>
        <p style={{ ...txt, color: tint.calloutText }}>{renderSegs(slide10aDuration, tint.calloutBold)}</p>
      </div>
    </div>
  );
};

export default HTLDSlide10aMobile;
